import {
    Badge,
    Box,
    Button,
    Card,
    Container,
    Divider,
    Group,
    List,
    Paper,
    PasswordInput,
    Stack,
    Stepper,
    Text,
    TextInput,
    Title
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft, IconBrandWhatsapp, IconCheck, IconHome, IconRocket, IconUser } from '@tabler/icons-react';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { addDoc, collection, doc, getDoc, setDoc } from 'firebase/firestore';
import { useState } from 'react';
import { auth, db } from '../../servicos/firebase';
import { MERCADO_PAGO_CONFIG } from '../../servicos/mercadopago';
import './RegistroNegocio.css';

const PLANS = [
    {
        id: 'profissional',
        name: 'Plano Profissional',
        price: 120.00,
        icon: '🚀',
        features: ['Até 20 Barbeiros', 'Agenda Online Ilimitada', 'Gestão Financeira VIP']
    }
];

const CYCLES = [
    { months: 1, name: 'Mensal', discount: 0 },
    { months: 3, name: 'Trimestral', discount: 5 },
    { months: 6, name: 'Semestral', discount: 10 },
    { months: 12, name: 'Anual', discount: 20 }
];

const RegistroNegocio = ({ onComplete, initialPlan, onBack }) => {
    const [activeStep, setActiveStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        plan: initialPlan || 'profissional',
        billingCycle: 1,
        ownerName: '',
        ownerEmail: '',
        ownerPassword: '',
        ownerWhatsapp: '',
        shopName: '',
        shopSlug: ''
    });
    const [finalGeneratedSlug, setFinalGeneratedSlug] = useState('');

    const nextStep = () => setActiveStep((current) => (current < 3 ? current + 1 : current));
    const prevStep = () => setActiveStep((current) => (current > 0 ? current - 1 : current));

    const updateFormData = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const formatSlug = (val) => {
        return val.toLowerCase()
            .replace(/ /g, '-')
            .replace(/[^\w-]/g, '');
    };

    const calculateTotal = () => {
        const plan = PLANS[0];
        const cycle = CYCLES.find(c => c.months === formData.billingCycle);
        const baseTotal = plan.price * cycle.months;
        return (baseTotal * (1 - cycle.discount / 100)).toFixed(2);
    };

    const handleSubmit = async () => {
        setLoading(true);
        try {
            // 0. Garantir link único (slug uniqueness check)
            let finalSlug = formData.shopSlug;
            let isUnique = false;
            let attempts = 0;

            while (!isUnique && attempts < 5) {
                const tenantSnap = await getDoc(doc(db, "tenants", finalSlug));
                if (tenantSnap.exists()) {
                    const randomSuffix = Math.floor(100 + Math.random() * 900);
                    finalSlug = `${formData.shopSlug}-${randomSuffix}`;
                    attempts++;
                } else {
                    isUnique = true;
                }
            }

            if (!isUnique) {
                throw new Error("Não foi possível gerar um link único. Tente um nome de barbearia diferente.");
            }

            setFinalGeneratedSlug(finalSlug);

            // 1. Criar Usuário no Firebase Auth
            const userCredential = await createUserWithEmailAndPassword(auth, formData.ownerEmail, formData.ownerPassword);
            const fbUser = userCredential.user;

            await updateProfile(fbUser, { displayName: formData.ownerName });

            // 2. Criar Perfil de Usuário no Firestore
            await setDoc(doc(db, "users", fbUser.uid), {
                name: formData.ownerName,
                email: formData.ownerEmail.toLowerCase(),
                whatsapp: formData.ownerWhatsapp,
                role: 'owner',
                barbershopId: finalSlug,
                createdAt: new Date().toISOString()
            });

            // 3. Criar a Unidade (Tenant) com 7 dias grátis
            const now = new Date();
            const trialEndDate = new Date();
            trialEndDate.setDate(now.getDate() + 7);

            await setDoc(doc(db, "tenants", finalSlug), {
                name: formData.shopName,
                adminEmails: [formData.ownerEmail.toLowerCase()],
                status: 'active',
                plan: formData.plan,
                createdAt: now.toISOString(),
                dueDate: trialEndDate.toISOString(), // Expiração do teste
                trialMode: true,
                billingCycle: formData.billingCycle
            });

            // 4. Salvar registro pendente para auditoria
            await addDoc(collection(db, "pending_registrations"), {
                ...formData,
                shopSlug: finalSlug, // Salva o link final gerado
                uid: fbUser.uid,
                status: 'trial_active',
                createdAt: new Date().toISOString(),
                totalPrice: calculateTotal()
            });

            notifications.show({
                title: 'Sucesso!',
                message: 'Sua barbearia foi criada e os 7 dias grátis estão ativos.',
                color: 'teal',
                icon: <IconCheck size={16} />,
            });

            setActiveStep(3); // Sucesso
        } catch (error) {
            console.error("Erro no cadastro:", error);
            let msg = error.message || "Erro ao processar cadastro.";

            if (error.code === 'auth/email-already-in-use') msg = "Este e-mail já está em uso.";
            if (error.code === 'auth/weak-password') msg = "A senha deve ter pelo menos 6 caracteres.";
            if (error.code === 'auth/invalid-email') msg = "O e-mail informado é inválido.";

            notifications.show({
                title: 'Erro no Cadastro',
                message: msg,
                color: 'red',
                autoClose: 10000, // Mais tempo para ler o erro
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="registration-page">
            <Button
                variant="subtle"
                color="gray"
                leftSection={<IconArrowLeft size={16} />}
                onClick={onBack}
                className="back-to-site-btn"
            >
                Voltar ao Site
            </Button>

            <Container size="sm">
                <Stack align="center" mb={40}>
                    <Title order={1} className="header-title" style={{ fontSize: 32 }}>Comece sua Jornada</Title>
                    <Text color="dimmed" size="lg" fw={500}>7 dias grátis para transformar sua barbearia.</Text>
                </Stack>

                <Stepper active={activeStep} onStepClick={setActiveStep} breakpoint="sm" mb={40}>
                    <Stepper.Step label="Teste" description="Início" icon={<IconRocket size={18} />}>
                        <Stack spacing="xl" align="center">
                            <Title order={3} align="center" fw={800} color="white">Tudo Pronto para Começar?</Title>
                            <Paper
                                p="xl"
                                radius="lg"
                                className="registration-card selected"
                                style={{ maxWidth: 400, width: '100%', textAlign: 'center' }}
                            >
                                <Stack spacing="xs">
                                    <Text fw={900} size="xl" color="#DAA520">7 DIAS GRÁTIS</Text>
                                    <Text size="sm" color="white" fw={600}>Teste todas as funcionalidades sem compromisso.</Text>
                                    <Divider my="sm" color="rgba(255,255,255,0.1)" />
                                    <List
                                        spacing="xs"
                                        size="sm"
                                        center
                                        icon={<IconCheck size={14} color="#DAA520" stroke={3} />}
                                        style={{ textAlign: 'left', color: 'rgba(255,255,255,0.7)' }}
                                    >
                                        <List.Item>Agenda Online Ilimitada</List.Item>
                                        <List.Item>Controle Financeiro</List.Item>
                                        <List.Item>Gestão de Barbeiros</List.Item>
                                    </List>
                                </Stack>
                            </Paper>
                            <Group justify="center">
                                <Button size="lg" className="btn-primary" onClick={nextStep} px={60}>Começar Teste Grátis</Button>
                            </Group>
                        </Stack>
                    </Stepper.Step>

                    <Stepper.Step label="Conta" description="Dados" icon={<IconUser size={18} />}>
                        <Stack spacing="md">
                            <TextInput label="Nome Completo" placeholder="Ex: João Silva" required value={formData.ownerName} onChange={(e) => updateFormData('ownerName', e.target.value)} />
                            <TextInput label="WhatsApp" placeholder="(34) 99999-9999" required value={formData.ownerWhatsapp} onChange={(e) => updateFormData('ownerWhatsapp', e.target.value)} />
                            <TextInput label="Email Profissional" placeholder="seu@email.com" required value={formData.ownerEmail} onChange={(e) => updateFormData('ownerEmail', e.target.value)} />
                            <PasswordInput label="Criar Senha" placeholder="********" required value={formData.ownerPassword} onChange={(e) => updateFormData('ownerPassword', e.target.value)} />
                            <Group justify="center" mt="xl">
                                <Button variant="default" className="btn-secondary" onClick={prevStep} px={30}>Voltar</Button>
                                <Button className="btn-primary" onClick={nextStep} disabled={!formData.ownerName || !formData.ownerEmail} px={30}>Próximo Passo</Button>
                            </Group>
                        </Stack>
                    </Stepper.Step>

                    <Stepper.Step label="Loja" description="Barbearia" icon={<IconHome size={18} />}>
                        <Stack spacing="md">
                            <TextInput
                                label="Nome da Barbearia"
                                placeholder="Ex: Barbearia do João"
                                required
                                value={formData.shopName}
                                onChange={(e) => {
                                    updateFormData('shopName', e.target.value);
                                    updateFormData('shopSlug', formatSlug(e.target.value));
                                }}
                            />
                            <Text size="xs" color="dimmed" mt={-10} fw={500}>
                                Seu link será: <span style={{ color: '#DAA520' }}>startwebb.com/{formData.shopSlug || '...'}</span>
                            </Text>

                            <Card radius="lg" p="xl" className="registration-card summary-card">
                                <Title order={4} mb="md" className="summary-title" fw={900}>Resumo do Cadastro</Title>
                                <Group justify="space-between" mb="xs">
                                    <Stack spacing={0}>
                                        <Text size="sm" color="white" fw={800}>Plano Profissional</Text>
                                        <Text size="xs" color="dimmed" fw={600}>7 Dias de Teste Grátis</Text>
                                    </Stack>
                                    <Badge color="green" variant="filled" size="lg" style={{ boxShadow: '0 0 15px rgba(64, 192, 87, 0.4)' }}>
                                        ATIVO
                                    </Badge>
                                </Group>
                                <Divider my="sm" color="rgba(255,255,255,0.1)" />
                                <Text size="sm" color="white" fw={600} align="center">
                                    Após os 7 dias de teste, você poderá escolher seu plano no painel na parte de assinatura.
                                </Text>
                            </Card>

                            <Group justify="center" mt="xl">
                                <Button variant="default" className="btn-secondary" onClick={prevStep} px={30}>Voltar</Button>
                                <Button className="btn-primary" onClick={handleSubmit} loading={loading} disabled={!formData.shopName} px={30}>Ativar meus 7 Dias Grátis</Button>
                            </Group>
                        </Stack>
                    </Stepper.Step>

                    <Stepper.Completed>
                        <Stack align="center" spacing="xl">
                            <Paper radius="50%" bg="#DAA520" p="xl" style={{ boxShadow: '0 0 30px rgba(218, 165, 32, 0.4)' }}>
                                <IconCheck size={40} color="black" stroke={3} />
                            </Paper>
                            <Box align="center">
                                <Title order={2} color="#DAA520" fw={900} style={{ fontSize: 28 }}>Seu Acesso está Liberado!</Title>
                                <Text size="lg" fw={700} color="white">Sua barbearia já está no ar.</Text>
                                <Text color="dimmed" fw={500}>Você tem 7 dias de acesso total para testar todas as funcionalidades.</Text>
                            </Box>

                            <Paper p="xl" radius="lg" w="100%" className="registration-card">
                                <Stack spacing="md">
                                    <Button
                                        size="xl"
                                        fullWidth
                                        className="btn-primary"
                                        onClick={() => onComplete(finalGeneratedSlug)}
                                    >
                                        IR PARA O MEU PAINEL (Link: {finalGeneratedSlug})
                                    </Button>

                                    <Divider label="ESCOLHA SEU PLANO DEPOIS" labelPosition="center" color="rgba(255,255,255,0.1)" />

                                    <Text size="sm" align="center" color="dimmed" fw={500}>
                                        Embora o acesso seja imediato, lembre-se que ao final dos 7 dias você deverá escolher um plano no seu painel na seção de assinatura para continuar utilizando o sistema.
                                    </Text>
                                </Stack>
                            </Paper>

                            <Button
                                variant="subtle"
                                color="green"
                                leftSection={<IconBrandWhatsapp size={20} />}
                                fullWidth
                                onClick={() => {
                                    const msg = `Olá! Acabei de ativar o teste de 7 dias para a barbearia *${formData.shopName}* (Link: startwebb.com/${finalGeneratedSlug}). Pode me dar um suporte inicial?`;
                                    window.open(`https://wa.me/${MERCADO_PAGO_CONFIG.supportWhatsapp}?text=${encodeURIComponent(msg)}`, '_blank');
                                }}
                            >
                                Falar com Suporte no WhatsApp
                            </Button>
                        </Stack>
                    </Stepper.Completed>
                </Stepper>
            </Container>
        </div>
    );
};

export default RegistroNegocio;
